import * as Sentry from '@sentry/react';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

export class ApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

function isJsonContentType(contentType: string | null): boolean {
  return !!contentType && contentType.includes('application/json');
}

function sanitizeErrorMessage(text: string): string {
  // If the server returned HTML (e.g., a Cloudflare error page), don't dump it into the UI.
  const trimmed = text.trim();
  if (trimmed.startsWith('<!DOCTYPE') || trimmed.startsWith('<html')) {
    return 'Server error, please try again later.';
  }
  if (trimmed.length > 200) {
    return trimmed.slice(0, 200) + '…';
  }
  return trimmed || 'Server error, please try again later.';
}

export type UserRole = 'site_engineer' | 'manager';

export interface UserProfile {
  id: string;
  email: string;
  full_name: string;
  role: UserRole;
  is_active?: boolean;
  created_at?: string;
}

export interface Project {
  id: string;
  project_number: string;
  name: string;
  client_name: string;
  location: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface Report {
  id: string;
  project_id: string;
  report_type: 'weekly' | 'monthly';
  report_number: string;
  week_ending_date: string;
  work_completed: string;
  issues_delays: string;
  next_week_plan: string;
  percent_complete: number | null;
  photos: Array<{ url: string; caption: string; path?: string }>;
  status: 'draft' | 'pending_review' | 'approved';
  created_by: string | null;
  submitted_at: string | null;
  approved_by: string | null;
  approved_at: string | null;
  manager_comments: string | null;
  created_at: string;
  updated_at: string;
  project?: { name: string; project_number: string; client_name?: string; location?: string };
  engineer?: { full_name: string; email?: string };
  approver?: { full_name: string; email?: string };
}

async function apiFetch(path: string, options: RequestInit = {}) {
  const url = `${API_BASE}${path}`;
  let res: Response;

  try {
    res = await fetch(url, {
      ...options,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(options.headers || {}),
      },
    });
  } catch (networkErr) {
    // fetch throws on network failures (offline, DNS, CORS, etc.)
    const message = networkErr instanceof Error ? networkErr.message : 'Network error';
    const err = new ApiError(message || 'Network error. Please check your connection.', 0);
    Sentry.captureException(networkErr, { extra: { context: `apiFetch ${path}`, url } });
    throw err;
  }

  const contentType = res.headers.get('content-type');
  const text = await res.text();

  // If the response isn't JSON, sanitize whatever we got back.
  if (!isJsonContentType(contentType) || !text) {
    const message = res.ok ? text : sanitizeErrorMessage(text);
    if (!res.ok) {
      throw new ApiError(message, res.status);
    }
    return message ? { message } : {};
  }

  let data: any = null;
  try {
    data = JSON.parse(text);
  } catch {
    const message = res.ok ? text : sanitizeErrorMessage(text);
    if (!res.ok) {
      throw new ApiError(message, res.status);
    }
    return { message };
  }

  if (!res.ok) {
    const message = data?.error || `HTTP ${res.status}`;
    throw new ApiError(message, res.status);
  }

  return data;
}

// ─── AUTH ──────────────────────────────────────────────────

export async function signInWithEmail(email: string, password: string) {
  return apiFetch('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
}

export async function signOut() {
  return apiFetch('/auth/logout', { method: 'POST' });
}

export async function getUserProfile(_userId: string): Promise<UserProfile | null> {
  try {
    const { user } = await apiFetch('/auth/me');
    return user || null;
  } catch {
    return null;
  }
}

export async function getCurrentSession() {
  try {
    const { user } = await apiFetch('/auth/me');
    return user ? { user } : null;
  } catch {
    return null;
  }
}

export function onAuthStateChange(_callback: (event: string, session: any) => void) {
  // In our custom auth, we don't have real-time auth state changes from a central source.
  // We simulate the subscription pattern by polling or just returning a no-op.
  // The UserProvider will handle state manually.
  return {
    data: {
      subscription: {
        unsubscribe: () => {},
      },
    },
  };
}

// ─── USERS ─────────────────────────────────────────────────

export async function createUser(
  email: string,
  fullName: string,
  role: UserRole,
  password?: string
) {
  return apiFetch('/users', {
    method: 'POST',
    body: JSON.stringify({ email, full_name: fullName, role, password }),
  });
}

export async function updateUserPassword(userId: string, newPassword: string) {
  return apiFetch(`/users/${userId}/update-password`, {
    method: 'POST',
    body: JSON.stringify({ newPassword }),
  });
}

export async function deleteUser(userId: string) {
  return apiFetch(`/users/${userId}`, { method: 'DELETE' });
}

export async function generateUserPassword(userId: string) {
  return apiFetch(`/users/${userId}/reset-password`, { method: 'POST' });
}

// ─── PROJECTS ──────────────────────────────────────────────

export async function getProjects(): Promise<Project[]> {
  const { projects } = await apiFetch('/projects');
  return projects || [];
}

export async function createProject(project: Omit<Project, 'id' | 'created_at' | 'updated_at'>) {
  return apiFetch('/projects', { method: 'POST', body: JSON.stringify(project) });
}

export async function updateProject(id: string, project: Partial<Project>) {
  return apiFetch(`/projects/${id}`, { method: 'PUT', body: JSON.stringify(project) });
}

export async function deleteProject(id: string) {
  return apiFetch(`/projects/${id}`, { method: 'DELETE' });
}

// ─── REPORTS ───────────────────────────────────────────────

export async function getReports(): Promise<Report[]> {
  const { reports } = await apiFetch('/reports');
  return reports || [];
}

export async function getReport(id: string): Promise<Report | null> {
  try {
    const { report } = await apiFetch(`/reports/${id}`);
    return report || null;
  } catch {
    return null;
  }
}

export async function createReport(report: Omit<Report, 'id' | 'created_at' | 'updated_at' | 'created_by'>) {
  return apiFetch('/reports', { method: 'POST', body: JSON.stringify(report) });
}

export async function updateReport(id: string, report: Partial<Report>) {
  return apiFetch(`/reports/${id}`, { method: 'PUT', body: JSON.stringify(report) });
}

export async function deleteReport(id: string) {
  return apiFetch(`/reports/${id}`, { method: 'DELETE' });
}

// ─── EMAIL ─────────────────────────────────────────────────

export async function sendEmail(to: string, type: string, data: Record<string, unknown>) {
  return apiFetch('/email/send', { method: 'POST', body: JSON.stringify({ to, type, data }) });
}

// ─── EXPORT / BACKUP ───────────────────────────────────────

export async function getAllReports(): Promise<Report[]> {
  return getReports();
}

export async function getAllProjects(): Promise<Project[]> {
  return getProjects();
}

export async function getAllProfiles(): Promise<UserProfile[]> {
  const { users } = await apiFetch('/users');
  return users || [];
}

// ─── STORAGE (inline base64 photos) ────────────────────────

export async function uploadPhoto(
  file: File,
  reportId: string,
  index: number
): Promise<{ url: string; path: string } | null> {
  try {
    const reader = new FileReader();
    const dataUrl = await new Promise<string>((resolve, reject) => {
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

    const { url, path } = await apiFetch('/upload/photo', {
      method: 'POST',
      body: JSON.stringify({ dataUrl, reportId, index: String(index) }),
    });

    return { url, path };
  } catch (err) {
    console.error('Error uploading photo:', err);
    Sentry.captureException(err, { extra: { context: 'Uploading photo' } });
    return null;
  }
}

export async function deletePhoto(_path: string): Promise<boolean> {
  // With inline base64 photos, there's nothing to delete from external storage.
  // The photo data lives in the reports.photos JSONB column.
  return true;
}

export async function deleteReportPhotos(_reportId: string): Promise<void> {
  // No-op for inline base64 photos
  return;
}

export function isStorageUrl(url: string): boolean {
  return url.startsWith('data:image/');
}

export function getPathFromUrl(url: string): string | null {
  if (url.startsWith('data:image/')) {
    return 'inline';
  }
  return null;
}

export async function createSignedUrl(path: string, _expiresIn?: number): Promise<string | null> {
  return path;
}

export async function getPhotosWithSignedUrls(
  photos: Array<{ url: string; caption: string; path?: string }>
): Promise<Array<{ url: string; caption: string; path?: string }>> {
  return photos;
}

// ─── HEALTH ────────────────────────────────────────────────

export async function checkApiConnection(): Promise<{ healthy: boolean; error?: string }> {
  try {
    await apiFetch('/auth/me');
    return { healthy: true };
  } catch (err: any) {
    return { healthy: false, error: err.message };
  }
}
